import { ApiError, ErrorCode } from '@/generated/api'
import { ApiError as MobileAuthApiError } from '@/generated/mobileAuthApi'
import log from 'loglevel'
import { AxiosError } from 'axios'
import { isProd } from './isprod'

function isErrorCode(v: unknown): v is ErrorCode {
  if (typeof v !== 'string') {
    return false
  }

  return (Object.values(ErrorCode) as string[]).includes(v)
}

function isApiError(v: unknown): v is ApiError | MobileAuthApiError {
  return v instanceof ApiError || v instanceof MobileAuthApiError
}
function isAxiosError(v: unknown): v is AxiosError {
  return v instanceof AxiosError
}
function errorMessageFromApiErrorMessage(body: object): string | null {
  if (!('message' in body)) {
    return null
  }

  const message = body.message

  if (typeof message !== 'string') {
    return null
  }

  return message
}
function errorMessageFromApiErrorDetail(body: object): string | null {
  if (!('detail' in body)) {
    return null
  }

  const detail = body.detail

  if (typeof detail !== 'string') {
    return null
  }

  return detail
}
function errorMessageFromApiErrorBody(body: unknown): string | null {
  if (typeof body === 'string') {
    return body
  }

  if (typeof body !== 'object') {
    return null
  }

  if (body === null) {
    return null
  }

  const detail = errorMessageFromApiErrorDetail(body)
  if (detail !== null) {
    return detail
  }

  const message = errorMessageFromApiErrorMessage(body)
  if (message !== null) {
    return message
  }

  return null
}

function errorMessageFromAxiosError(error: AxiosError): string {
  if (error.response === undefined) {
    const requestConfig = error?.config
    if (!isProd() && requestConfig) {
      // Show more detailed error message if outside prod env for easier
      // troubleshooting (i.e. debugging, host misconfiguration).
      const detailedMessage = `Request ${requestConfig.method?.toUpperCase() ?? '???'} ${requestConfig.url} failed with error: ${error.message}`
      log.debug(detailedMessage, requestConfig)
      return detailedMessage
    }

    return error.message ?? JSON.stringify(error.response)
  }

  const body = error.response.data
  return errorMessageFromApiErrorBody(body) ?? JSON.stringify(error)
}

function errorMessageFromApiError(error: ApiError | MobileAuthApiError): string {
  return errorMessageFromApiErrorBody(error.body) ?? JSON.stringify(error)
}

function errorCodeFromApiErrorBody(body: object): ErrorCode | null {
  if (!('errorCode' in body)) return null

  if (isErrorCode(body.errorCode)) {
    return body.errorCode
  }
  return null
}

/**
 * NOTE: Clash of notation
 * On the BE there is the ApiError class whose instances
 * are represented with error code.
 * However, the ApiError class here represents simply any
 * error response from the BE. This name is auto-generated.
 */
function errorCodeFromApiError(error: ApiError): ErrorCode | null {
  if (error.body === null) {
    return null
  }

  if (typeof error.body !== 'object') {
    return null
  }

  return errorCodeFromApiErrorBody(error.body)
}

/**
 * When error response is not generated by ApiError on the BE, this function returns null
 */
export function errorCodeFromUnknownError(error: unknown): ErrorCode | null {
  if (!isApiError(error)) {
    return null
  }

  return errorCodeFromApiError(error)
}

// Method tries to match unknown error with one of supported error classes
// and return that error message in human readable format.
export function errorMessageFromUnknownError(error: unknown): string {
  if (isApiError(error)) {
    return errorMessageFromApiError(error)
  } else if (isAxiosError(error)) {
    return errorMessageFromAxiosError(error)
  } else if (error instanceof Error) {
    return error.message
  } else if (typeof error === 'string') {
    return error
  }

  return JSON.stringify(error)
}

export function isAccessTokenExpiredError(error: unknown): boolean {
  return errorMessageFromUnknownError(error) === 'Access token is expired'
}

export function isRefreshTokenExpiredError(error: unknown): boolean {
  return errorMessageFromUnknownError(error) === 'Refresh token is expired'
}

export function isRefreshTokenAlreadyUsedError(error: unknown): boolean {
  return errorMessageFromUnknownError(error) === 'Refresh token already used'
}
